import type { AffiliateCommissions } from "@decibeltrade/sdk";

import type { RewardsSummary } from "@/contexts/queries/rewards/use-rewards-summary";

/** What the gold cell renders once the accrual ledger has something to say. */
export interface AccrualView {
  /** Accrued since Monday — what the weekly sweep will pay, or forfeit. */
  weekUsd: number;
  /** The floor the week has to clear to be paid at all. */
  minimumUsd: number;
  /** How much more the week needs. Zero once the floor is cleared. */
  shortfallUsd: number;
  meetsMinimum: boolean;
  /** 0.0–1.0, for a progress indicator. */
  progress: number;
  /**
   * The week before this one, as accrued history. Null when it is zero, forfeited or withheld:
   * "$0.00 from last week" is noise, and a week the sweep held back is answered on the
   * commissions page, not in a hero cell.
   */
  previous: PreviousWeek | null;
}

export interface PreviousWeek {
  usd: number;
  /** True once the sweep has decided; false while the week is still waiting to be swept. */
  swept: boolean;
}

/**
 * The previous week, or null when there is nothing worth a line of caption.
 *
 * A week closes Sunday and the sweep runs Monday 06:00 UTC, so for a while the running week
 * reads near zero while last week's figure is the one that matters. It stays as history: what
 * is claimable is answered separately, from the on-chain campaigns, so this line never has to
 * guess at a payday.
 */
function toPreviousWeek(data: AffiliateCommissions): PreviousWeek | null {
  if (data.previous_week_status === "forfeited") return null;
  if (data.previous_week_status === "withheld") return null;
  if (data.previous_week_usd <= 0) return null;

  // An empty status means the sweep has not run. That is the state worth telling the
  // affiliate about, so it is reported rather than filtered out.
  return { usd: data.previous_week_usd, swept: data.previous_week_status !== "" };
}

/**
 * Reads the accrual view out of the commissions response, or null when the ledger has
 * nothing for this affiliate.
 *
 * Null is the signal to fall back to the fee basis, and it has to mean "no ledger data",
 * not "nothing earned". An affiliate whose week accrued exactly $0 while the ledger holds
 * rows for them has a real, reportable answer — falling back there would replace a correct
 * $0 with a fee-revenue figure that reads like earnings.
 *
 * So the test is on `days`, not on the amount: rows exist, therefore the accrual is
 * authoritative. A deployment where the ledger table has not been created yet returns no
 * days and lands on the basis, which is the pre-ledger behaviour.
 */
export function toAccrualView(data: AffiliateCommissions | undefined): AccrualView | null {
  if (data === undefined || data.days.length === 0) {
    return null;
  }

  const weekUsd = data.accrued_this_week_usd;
  const minimumUsd = data.minimum_payout_usd;

  return {
    previous: toPreviousWeek(data),
    weekUsd,
    minimumUsd,
    // Clamped at zero: a week already over the floor has no shortfall, and a net-rebated
    // week that accrued negative should read as needing the full minimum, not more than it.
    shortfallUsd: Math.max(0, minimumUsd - Math.max(0, weekUsd)),
    meetsMinimum: data.meets_minimum,
    progress: data.progress_to_minimum,
  };
}

/**
 * USDC the affiliate can claim right now across every affiliate campaign, from the same
 * on-chain summary the Rewards page shows. Zero while the summary has not loaded: the claim
 * row is an invitation, and inviting before knowing is worse than a moment's delay.
 */
export function affiliateClaimableUsd(summary: RewardsSummary | undefined): number {
  if (!summary) return 0;
  return summary.breakdownByType
    .filter((row) => row.category === "affiliate")
    .reduce((sum, row) => sum + row.readyToClaimUsd, 0);
}
